<template>
  <div class="login-overlay" :class="{ 'login-overlay--inline': !embedded }">
    <div class="login-card">

      <div v-if="state === 'checking'" class="loading">{{ t('loading') }}</div>

      <LoginView
        v-else-if="state === 'unauthenticated'"
        :initial-email="initialEmailComputed"
        :lock-email="lockEmail"
        :busy="busy"
        :error-message="errorMessage"
        :info-message="infoMessage"
        @submit="onLoginSubmit"
        @forgot="state = 'forgotPassword'"
        @register="state = 'register'"
        @close="onClose"
      />

      <ForgotPasswordView
        v-else-if="state === 'forgotPassword'"
        :initial-email="lastEmail"
        :busy="busy"
        :error-message="errorMessage"
        :info-message="infoMessage"
        @submit="onForgotSubmit"
        @back="backToLogin"
      />

      <RegisterView
        v-else-if="state === 'register'"
        :busy="busy"
        :error-message="errorMessage"
        :info-message="infoMessage"
        @submit="onRegisterSubmit"
        @back="backToLogin"
      />

      <RolePickerView
        v-else-if="state === 'rolePicker'"
        @pick="onRolePicked"
        @cancel="cancelToLogin"
      />

      <AccountPickerView
        v-else-if="state === 'accountPicker'"
        :kind="pickerKind"
        :accounts="pickerAccounts"
        :preselect="preselectAccountObject"
        :busy="busy"
        :error-message="errorMessage"
        @pick="onAccountPicked"
        @cancel="cancelToLogin"
      />

      <LockedView
        v-else-if="state === 'locked'"
        :locked-until="lockedUntil"
        @retry="backToLogin"
      />

      <TotpView
        v-else-if="state === 'totpRequired'"
        :session="session"
        :busy="busy"
        :error-message="errorMessage"
        @verified="onTotpVerified"
        @cancel="onCancelDuringAuth"
      />

      <ChangePasswordView
        v-else-if="state === 'tempPasswordChange'"
        :domain="session.domain || 'tibtix.com'"
        :forced="true"
        :initial-old-password="tempPassword"
        :busy="busy"
        :error-message="errorMessage"
        @changed="onPasswordChanged"
        @logout="onCancelDuringAuth"
      />

      <TermsView
        v-else-if="state === 'termsRequired'"
        :terms-status="termsStatus"
        :busy="busy"
        :error-message="errorMessage"
        @accepted="onTermsAccepted"
        @cancel="onCancelDuringAuth"
      />

    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import LoginApiService from './services/LoginApiService.js'
import { useI18n, applyMessageOverrides, setLocale } from './i18n/index.js'

import LoginView from './views/LoginView.vue'
import ForgotPasswordView from './views/ForgotPasswordView.vue'
import RegisterView from './views/RegisterView.vue'
import RolePickerView from './views/RolePickerView.vue'
import AccountPickerView from './views/AccountPickerView.vue'
import LockedView from './views/LockedView.vue'
import TotpView from './views/TotpView.vue'
import ChangePasswordView from './views/ChangePasswordView.vue'
import TermsView from './views/TermsView.vue'

// ----------------------------------------------------------------------
// LoginOrchestrator — single source of truth for the auth flow.
//
// State machine (transitions are driven only by server responses):
//
//     checking
//        │
//        ▼
//   unauthenticated ──forgot──▶ forgotPassword ──back──▶ unauthenticated
//        │                                ▲
//        │                                │
//        ├──register──▶ register ─────────┘
//        │
//        ▼ submit
//   authenticating (transient — login POST in flight)
//        │
//        ├─ locked            ──retry──▶ unauthenticated
//        ├─ totpRequired      ──verified──▶ (next gate)
//        ├─ tempPasswordChange ──changed──▶ (next gate)
//        ├─ termsRequired     ──accepted──▶ (next gate)
//        └─ authenticated     ──emit logged-in──▶ host
//
// `authenticating` is not its own visible state — we keep `busy=true`
// while the login POST is in flight and stay on `unauthenticated`.
// ----------------------------------------------------------------------

export default defineComponent({
  name: 'LoginOrchestrator',

  components: {
    LoginView, ForgotPasswordView, RegisterView,
    RolePickerView, AccountPickerView,
    LockedView, TotpView, ChangePasswordView, TermsView
  },

  props: {
    // Embedded = floating modal overlay (default).
    // !embedded = inline full-page (used by the JSP shell where the
    // page IS the login and there's no host content behind it).
    embedded: { type: Boolean, default: true },

    // Optional: pre-fill the email (e.g. JSP renders `?emails=` URL
    // params from a deep link). Otherwise we pick from `lastusername`
    // cookie.
    initialEmail: { type: String, default: '' },
    lockEmail: { type: Boolean, default: false },

    // Deep-link hints from a notification email. After the discovery login
    // we use them to auto-select (and skip the picker for) the matching
    // admin domain-account / client connection:
    //   initialDomain     → domainName of an admin domain-account
    //   initialAccount    → accountName; with initialDomain narrows the
    //                       hint to one exact admin domain-account
    //   initialConnection → connectionId of a client connection
    initialDomain: { type: String, default: '' },
    initialAccount: { type: String, default: '' },
    initialConnection: { type: String, default: '' },

    // Where to redirect after a successful login. JSP login shell
    // passes `originalRequestURLWithHash`; Vue apps usually leave
    // this null and react to the `logged-in` event instead.
    redirectAfterLogin: { type: String, default: '' },

    // i18n locale override ('de' / 'en'); falls back to browser detection.
    locale: { type: String, default: '' },

    // Optional override map: { de: { ... }, en: { ... } } merged on top of
    // the bundled JSON.
    messages: { type: Object, default: () => ({}) },

    // If true, mount in a "guard" mode that auto-runs checkLogin first
    // and only opens the modal when the session is missing. Useful for
    // app-startup integration (TibtixLogin.guard()).
    guardMode: { type: Boolean, default: false }
  },

  emits: ['logged-in', 'logged-out', 'close', 'state-change'],

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    return {
      state: 'checking',
      busy: false,
      errorMessage: '',
      infoMessage: '',
      // Most-recent email entered — used to pre-fill the reset view
      // and to short-cut a "remembered" username on return visits.
      lastEmail: '',
      // After /login we may need follow-up gates; we keep the password
      // around so the change-password view can pre-fill the "old"
      // field with the temp password (mirrors ChatLogin.vue).
      tempPassword: '',
      session: null,
      // For LockedView countdown.
      lockedUntil: 0,
      // Cached for TermsView.
      termsStatus: null,
      // Cookie pre-fill, read once at mount.
      rememberedDomainAccount: this.readCookiePair(),

      // ---- Discovery / picker state ---------------------------------
      // Discovery-login response, kept while the user walks the picker.
      connections: [],
      adminDomainAccounts: [],
      // Email + password are stashed in memory between the discovery
      // login and the (optional) admin re-login. Cleared the moment we
      // either move past the picker or cancel back to the login form.
      credentials: null,
      // 'admin' | 'client' — drives which list AccountPickerView shows.
      pickerKind: '',
      // Effective redirect target after completeLogin(). Defaults to
      // the `redirectAfterLogin` prop, but the client-pick branch
      // overrides it with the chosen connection's folder URL so the
      // user lands directly in their connection after picking it.
      pickedRedirectUrl: '',
      // Server switch hashhash.ui.client: clients go to the new UI (/tt/ui), so they never pick a connection
      // here — whatever the return target (the server redirects old client pages into /tt/ui).
      clientUi: false
    }
  },

  computed: {
    initialEmailComputed() {
      return this.initialEmail || this.readCookie('lastusername') || ''
    },
    /** AccessibleAccount[] currently displayed by AccountPickerView. */
    pickerAccounts() {
      return this.pickerKind === 'admin'
        ? this.adminDomainAccounts
        : this.connections
    },
    /** Returns an AccessibleAccount from pickerAccounts that matches the
     *  lastaccountname / lastdomainname cookies, or null. */
    rememberedAccountObject() {
      const acc = this.readCookie('lastaccountname')
      const dom = this.readCookie('lastdomainname')
      if (!acc || !dom) return null
      return this.pickerAccounts.find(
        a => a.accountName === acc && a.domainName === dom
      ) || null
    },
    /** Account to preselect in the AccountPickerView. A deep-link hint
     *  (admin domain / client connection) wins over the remembered cookie
     *  so a recipient opening a notification email lands on the right entry. */
    preselectAccountObject() {
      if (this.pickerKind === 'admin' && this.initialDomain) {
        const m = this.adminDomainAccounts.find(a =>
          a.domainName === this.initialDomain &&
          (!this.initialAccount || a.accountName === this.initialAccount))
        if (m) return m
      }
      if (this.pickerKind === 'client' && this.initialConnection) {
        const m = this.connections.find(c => c.connectionId === this.initialConnection)
        if (m) return m
      }
      return this.rememberedAccountObject
    },
    /** True when the post-login target is the new /tt/ui SPA. Then a client
     *  logs in *normally* and lands there — the connection list is the SPA's
     *  own folder root (chosen inside /tt/ui, not here). login-ui must then
     *  NOT show a client connection picker and NOT divert to the old
     *  /tt/view/connection/{id}/folder JSP. Domain-user (admin) picks and the
     *  old JSP login shell (any other return target) are unaffected. */
    returnsToNewUi() {
      const r = this.redirectAfterLogin
      if (!r) return false
      let path = r
      try {
        path = new URL(r, window.location.origin).pathname
      } catch (e) { /* r is already a bare path */ }
      return path === '/tt/ui' || path.indexOf('/tt/ui/') === 0
    }
  },

  watch: {
    state(value) {
      this.$emit('state-change', value)
    },
    locale: {
      immediate: true,
      handler(value) {
        if (value) setLocale(value)
      }
    },
    messages: {
      immediate: true,
      handler(value) {
        if (value && Object.keys(value).length) applyMessageOverrides(value)
      }
    }
  },

  async mounted() {
    this.uiConfigLoaded = LoginApiService.uiConfig()
      .then(c => { this.clientUi = !!(c && c.clientUi) })
      .catch(() => {})
    if (this.guardMode) {
      const session = await LoginApiService.checkLogin()
      if (session && this.sessionSatisfiesHint(session)) {
        // Already authenticated AND the current session can serve the
        // deep-link hint (right account/role) — short-circuit, but still
        // run the post-auth gates (terms, possibly TOTP re-prompt) before
        // emitting.
        this.session = session
        await this.runPostAuthGates(session)
        return
      }
      // else: not authenticated, OR the current session does NOT match the
      // deep-link hint (wrong account/role). Fall through to a fresh login so
      // routeAfterDiscovery() selects the hinted account/connection instead of
      // landing on the chat with a mismatched session.
    }
    this.state = 'unauthenticated'
  },

  methods: {

    // -------- Cookie helpers --------------------------------------

    readCookie(name) {
      const parts = typeof document !== 'undefined' && document.cookie
        ? document.cookie.split(';')
        : []
      for (const p of parts) {
        const [k, ...rest] = p.split('=')
        if (k && k.trim() === name) {
          try { return decodeURIComponent(rest.join('=')) }
          catch (e) { return rest.join('=') }
        }
      }
      return ''
    },
    readCookiePair() {
      const account = this.readCookie('lastaccountname')
      const domain = this.readCookie('lastdomainname')
      return account && domain ? `${account}##${domain}` : ''
    },

    // -------- Login handlers --------------------------------------

    /**
     * Step 1 — Discovery login. The user only types email + password;
     * the server validates them on the tibtix.com client path and
     * returns the lists of admin domain-accounts and client
     * connections. We then route into the pickers (or skip them if
     * there's no actual choice).
     */
    async onLoginSubmit({ username, password }) {
      this.lastEmail = username
      this.errorMessage = ''
      this.infoMessage = ''
      this.busy = true
      try {
        const data = await LoginApiService.discoverLogin({ username, password })

        if (!data.loggedIn) {
          if (data.errorCode === 'USER_LOGIN_LOCKED' && data.lockedUntil > 0) {
            this.lockedUntil = data.lockedUntil
            this.state = 'locked'
          } else {
            this.errorMessage = this.formatLoginError(data)
          }
          return
        }

        // Stash credentials for a possible admin re-login + the
        // accessible-account lists for the picker.
        this.credentials = { username, password }
        this.connections = Array.isArray(data.connections) ? data.connections : []
        this.adminDomainAccounts = Array.isArray(data.adminDomainAccounts)
          ? data.adminDomainAccounts
          : []

        // Build a provisional session from the discovery response — the
        // pickers may overwrite domain/account, post-auth gates need it
        // for the locked / temp-pw / terms branches that don't depend
        // on the picker outcome.
        const roles = Array.isArray(data.roles) ? data.roles : []
        this.session = {
          username,
          password,
          domain: data.domainName || 'tibtix.com',
          domainAccountName: data.accountName || '',
          clientLogin: true, // discovery is always a client login
          roles,
          loggedInWithTempPassword: !!data.loggedInWithTempPassword
        }
        this.tempPassword = data.loggedInWithTempPassword ? password : ''

        // Temp-password gate jumps the queue: forced PW change before
        // the user gets to pick a role / account. After the change we
        // re-route through routeAfterDiscovery().
        if (this.session.loggedInWithTempPassword) {
          this.state = 'tempPasswordChange'
          return
        }

        await this.routeAfterDiscovery()
      } catch (err) {
        console.error('discoverLogin failed', err)
        this.errorMessage = this.t('errorGeneric')
      } finally {
        this.busy = false
      }
    },

    /**
     * True if an already-authenticated session can serve the current
     * deep-link hint (from a notification email). Ohne Hint ist jede Session
     * ok. Ein Client-Connection-Hint braucht eine Client-Session (chat-ui
     * listet dann alle Verbindungen des Clients und wählt die gehintete).
     * Ein Admin-Hint (domain[+account]) braucht eine Domain-User-Session auf
     * genau diesem Konto. Bei Mismatch (falsches Konto/falsche Rolle) wird neu
     * angemeldet, damit der Hint das richtige Konto/die richtige Verbindung
     * wählen kann.
     */
    sessionSatisfiesHint(session) {
      if (!session) return false
      if (this.initialConnection) {
        return session.clientLogin === true
      }
      if (this.initialDomain) {
        return session.clientLogin === false
          && session.domain === this.initialDomain
          && (!this.initialAccount || session.domainAccountName === this.initialAccount)
      }
      return true
    },

    /**
     * Decides where to land after a successful discovery login. Rules:
     *   - both lists non-empty                → rolePicker
     *   - only admin domain-accounts          → pickerKind=admin, auto-skip if length===1
     *   - only client connections             → pickerKind=client, auto-skip if length===1
     *   - both lists empty                    → completeLogin (no choice to make)
     */
    async routeAfterDiscovery() {
      const adminCount = this.adminDomainAccounts.length
      const connCount  = this.connections.length

      // Deep-link hints from a notification email take precedence: jump
      // straight to the hinted admin domain or client connection and skip
      // the role/account pickers. If a hint matches several accounts we
      // still show the picker, but preselected (preselectAccountObject).
      const adminHint = this.initialDomain
        ? this.adminDomainAccounts.filter(a =>
            a.domainName === this.initialDomain &&
            (!this.initialAccount || a.accountName === this.initialAccount))
        : []
      const connHint = this.initialConnection
        ? this.connections.filter(c => c.connectionId === this.initialConnection)
        : []

      if (adminHint.length === 1) {
        this.pickerKind = 'admin'
        return this.finalizeWith(adminHint[0])
      }
      if (connHint.length === 1) {
        this.pickerKind = 'client'
        // Keep the email's `return` (chat deep link) as the landing target
        // instead of overriding it with the connection folder view.
        return this.finalizeWith(connHint[0], { preserveReturn: true })
      }
      if (adminHint.length > 1) {
        this.pickerKind = 'admin'
        this.state = 'accountPicker'
        return
      }
      if (connHint.length > 1) {
        this.pickerKind = 'client'
        this.state = 'accountPicker'
        return
      }

      if (adminCount > 0 && connCount > 0) {
        this.state = 'rolePicker'
        return
      }
      if (adminCount > 0) {
        return this.openAccountPicker('admin')
      }
      if (connCount > 0) {
        return this.openAccountPicker('client')
      }
      // No accessible accounts — keep the discovery session (client on
      // tibtix.com) and walk the remaining gates (TOTP / terms).
      await this.runPostAuthGates(this.session)
    },

    /**
     * Show the AccountPickerView for the chosen kind, or short-circuit
     * to finalisation when there's only one entry to pick from.
     */
    async openAccountPicker(kind) {
      this.pickerKind = kind
      // New UI: a client NEVER picks a connection in login-ui. The connection
      // list is always shown first inside /tt/ui (the tib browser) when the
      // client has more than one connection. This holds for a pure client AND
      // for a dual-role user (also a domain admin) who chose the client role:
      // the discovery login already established a client session (server side
      // `username + "#"`, isClientLogin=true), so we skip the picker entirely
      // and run the post-auth gates, landing on the ?return (/tt/ui) target.
      // Only the Domain-Admin role goes through an account picker.
      if (kind === 'client') {
        if (this.uiConfigLoaded) await this.uiConfigLoaded
        if (this.returnsToNewUi || this.clientUi) {
          await this.runPostAuthGates(this.session)
          return
        }
      }
      const list = this.pickerAccounts
      if (list.length === 1) {
        // Single option — no point in showing a one-element radio list.
        await this.finalizeWith(list[0])
        return
      }
      this.state = 'accountPicker'
    },

    onRolePicked(role) {
      // role = 'admin' | 'client'
      this.errorMessage = ''
      this.openAccountPicker(role)
    },

    async onAccountPicked(account) {
      this.errorMessage = ''
      await this.finalizeWith(account)
    },

    /**
     * Step 2 — commit the chosen role/account.
     *   - Admin path:  second POST /api/{domain}/login with
     *                  domainAccountName=accountName##domain, replacing
     *                  the discovery session with a domain-bound one.
     *   - Client path: POST /api/ticketing/context picks the connection
     *                  on the existing session — no PW required.
     * Then re-walks the post-auth gates against the now-final session.
     */
    async finalizeWith(account, opts = {}) {
      if (!account) return
      this.busy = true
      try {
        if (this.pickerKind === 'admin') {
          if (!this.credentials) {
            this.errorMessage = this.t('errorGeneric')
            this.state = 'unauthenticated'
            return
          }
          const data = await LoginApiService.login({
            username: this.credentials.username,
            password: this.credentials.password,
            domainAccountName: account.accountName + '##' + account.domainName,
            domain: account.domainName
          })
          if (!data.loggedIn) {
            if (data.errorCode === 'USER_LOGIN_LOCKED' && data.lockedUntil > 0) {
              this.lockedUntil = data.lockedUntil
              this.state = 'locked'
            } else {
              this.errorMessage = this.formatLoginError(data)
              this.state = 'accountPicker'
            }
            return
          }
          const roles = Array.isArray(data.roles) ? data.roles : []
          this.session = {
            username: this.credentials.username,
            password: this.credentials.password,
            domain: data.domainName || account.domainName,
            domainAccountName: data.accountName || account.accountName,
            clientLogin: false,
            roles,
            loggedInWithTempPassword: !!data.loggedInWithTempPassword
          }
        } else {
          // Client connection switch — re-uses the discovery session.
          try {
            await LoginApiService.setClientContext({
              accountName: account.accountName,
              domainName:  account.domainName
            })
          } catch (e) {
            this.errorMessage = (e && e.message) || this.t('errorGeneric')
            this.state = 'accountPicker'
            return
          }
          // Patch the session so post-auth gates target the chosen
          // account (TOTP probes per-account).
          this.session = {
            ...this.session,
            domain: account.domainName,
            domainAccountName: account.accountName,
            clientLogin: true
          }
          // After a client-side pick, the natural landing page is the
          // connection's folder view. Overrides the generic `return=`
          // target for this flow only — admin picks keep the original
          // return URL. When the caller asked to preserve the return URL
          // (deep link from an email pointing straight at the chat), we
          // leave pickedRedirectUrl empty so that return target wins.
          if (account.connectionId && !opts.preserveReturn && !this.returnsToNewUi) {
            this.pickedRedirectUrl = '/tt/view/connection/'
              + encodeURIComponent(account.connectionId) + '/folder'
          }
        }

        // Credentials no longer needed past this point. Wipe.
        this.credentials = null
        await this.runPostAuthGates(this.session)
      } finally {
        this.busy = false
      }
    },

    /**
     * Back-out of the picker — drop the discovery session server-side
     * and return to the unauthenticated login form.
     */
    cancelToLogin() {
      this.credentials = null
      this.connections = []
      this.adminDomainAccounts = []
      this.pickerKind = ''
      this.pickedRedirectUrl = ''
      LoginApiService.logout().finally(() => {
        this.session = null
        this.tempPassword = ''
        this.errorMessage = ''
        this.state = 'unauthenticated'
        this.$emit('logged-out')
      })
    },

    /**
     * Linear gate-check after a successful POST /login:
     *   1. Temp password? → ChangePasswordView (forced).
     *   2. needTotp + (need setup or recurring code)? → TotpView.
     *   3. Terms version drifted? → TermsView.
     *   4. Otherwise → done.
     */
    async runPostAuthGates(session) {
      // 1. Temp password — the LoginResponseModel flag is the trigger.
      if (session.loggedInWithTempPassword) {
        this.state = 'tempPasswordChange'
        return
      }
      // 2. TOTP — the needTotp probe is an account-level (domain-user) gate:
      // it resolves a concrete domain-account and asks whether that account
      // requires a second factor for this session. A pure client has no such
      // picked account (session.domain is the client pseudo-domain '#', or the
      // account is empty) — its own 2FA is already enforced at authentication
      // time (DatabaseAuthenticationProvider, token.isClient()). Probing '#'/
      // empty would only 404, so skip it for that case. Domain-user picks
      // always carry a real domain + account here and are unaffected.
      const hasAccountToProbe = session.domain && session.domain !== '#' && !!session.domainAccountName
      try {
        const need = hasAccountToProbe
          ? await LoginApiService.needTotp(session.domain, session.domainAccountName)
          : false
        if (need) {
          this.state = 'totpRequired'
          return
        }
      } catch (e) {
        // Treat probe failure as "no TOTP required" — defensive,
        // mirrors how chat-ui handles unknown optional flows.
      }
      // 3. Terms — only client users have a terms flow.
      if (session.clientLogin) {
        try {
          const status = await LoginApiService.getTermsStatus()
          if (status && status.requiresAcceptance) {
            this.termsStatus = status
            this.state = 'termsRequired'
            return
          }
        } catch (e) { /* terms unreachable: don't block */ }
      }
      // 4. Done.
      this.completeLogin()
    },

    onTotpVerified() {
      // Carry on with the remaining gates after a successful 2FA.
      // The orchestrator's runPostAuthGates re-walks them from after
      // the totp step (cheap repeat — needTotp is now false because
      // the token's `totpActivated` is set).
      this.continuePostTotp()
    },
    async continuePostTotp() {
      if (!this.session) return
      // Skip steps 1+2, jump to terms.
      if (this.session.clientLogin) {
        try {
          const status = await LoginApiService.getTermsStatus()
          if (status && status.requiresAcceptance) {
            this.termsStatus = status
            this.state = 'termsRequired'
            return
          }
        } catch (e) { /* ignore */ }
      }
      this.completeLogin()
    },

    onPasswordChanged(newPassword) {
      // Temp-password swapped for a real one. Clear the temp marker and
      // continue. If we're still pre-picker (no account chosen yet),
      // re-route through the discovery branches; otherwise the change
      // happened after an admin re-login and we continue with the
      // remaining gates as before.
      if (this.session) this.session.loggedInWithTempPassword = false
      this.tempPassword = ''
      // Update stashed credentials so a subsequent admin re-login uses
      // the new password (ChangePasswordView passes it as the argument).
      if (typeof newPassword === 'string' && newPassword.length > 0 && this.credentials) {
        this.credentials.password = newPassword
        this.session.password = newPassword
      }
      if (this.credentials) {
        // Pre-picker: still in discovery phase, walk into the picker.
        this.routeAfterDiscovery()
        return
      }
      this.runPostAuthGates(this.session)
    },

    onTermsAccepted() {
      this.completeLogin()
    },

    completeLogin() {
      const session = this.session
      this.state = 'authenticated'
      // Picker-set target wins over the generic `?return=` URL. For
      // a client pick we land directly in the chosen connection's
      // folder; admin picks and gate-only flows fall through to the
      // host-supplied redirect.
      const target = this.pickedRedirectUrl || this.redirectAfterLogin
      this.$emit('logged-in', session, target)
      if (target) {
        try {
          window.location.href = target
        } catch (e) { /* ignore */ }
      }
    },

    // -------- Forgot password -------------------------------------

    async onForgotSubmit(email) {
      this.errorMessage = ''
      this.infoMessage = ''
      this.busy = true
      try {
        const res = await LoginApiService.forgotPassword(email)
        if (res && res.success) {
          this.infoMessage = this.t('resetPasswordSent', { email })
          this.lastEmail = email
          this.state = 'unauthenticated'
        } else {
          this.errorMessage = (res && res.message) || this.t('resetPasswordFailed')
        }
      } catch (err) {
        this.errorMessage = this.t('resetPasswordFailed')
      } finally {
        this.busy = false
      }
    },

    // -------- Register --------------------------------------------

    async onRegisterSubmit(payload) {
      // Phase-1: the RegisterView guards itself behind a feature flag
      // (no backend endpoint may exist). If it ever emits we treat it
      // as best-effort and surface whatever the server says.
      this.errorMessage = ''
      this.infoMessage = ''
      this.busy = true
      try {
        // No dedicated endpoint helper yet — orchestrator delegates
        // to whatever the view configures. Phase-2 work.
        if (payload && payload.success) {
          this.infoMessage = this.t('registerSuccess')
          this.state = 'unauthenticated'
        } else {
          this.errorMessage = (payload && payload.message) || this.t('registerFailed')
        }
      } finally {
        this.busy = false
      }
    },

    // -------- Navigation helpers ----------------------------------

    backToLogin() {
      this.errorMessage = ''
      this.state = 'unauthenticated'
    },

    /**
     * Public entry-points for the StandaloneShell menu. Called via
     * `$refs.orchestrator.goToRegister()` so the header navigation can
     * drive the state-machine without each menu entry duplicating the
     * routing rules. Both are no-ops while a session is being walked
     * through (locked / totp / tempPw / terms) to avoid blowing away
     * progress mid-gate.
     */
    goToRegister() {
      if (this.session && this.state !== 'unauthenticated' && this.state !== 'register') return
      this.errorMessage = ''
      this.infoMessage = ''
      this.state = 'register'
    },
    goToLogin() {
      if (this.session && !['unauthenticated', 'register', 'forgotPassword'].includes(this.state)) return
      this.errorMessage = ''
      this.infoMessage = ''
      this.state = 'unauthenticated'
    },
    onCancelDuringAuth() {
      // Cancel during temp-pw / TOTP / terms means: drop the session
      // server-side and return to the login form.
      LoginApiService.logout().finally(() => {
        this.session = null
        this.tempPassword = ''
        this.errorMessage = ''
        this.state = 'unauthenticated'
        this.$emit('logged-out')
      })
    },
    onClose() {
      // Embedded (modal): der Host schliesst das Login. Standalone (guardMode): es gibt nichts zu schliessen —
      // die Schale nutzt das Signal, um den vorgelagerten Hinweis wieder zu zeigen (das „X" war sonst funktionslos).
      // Reine JSP-Shells (weder embedded noch guardMode) ignorieren es weiterhin.
      if (!this.embedded && !this.guardMode) return
      this.$emit('close')
    },

    // -------- Error formatting ------------------------------------

    formatLoginError(data) {
      const code = data && data.errorCode
      if (code === 'USER_LOGIN_LOCKED') {
        const until = data.lockedUntil ? new Date(data.lockedUntil).toLocaleString() : ''
        return this.t('errorLoginLocked', { until })
      }
      if (code === 'TEMP_PASSWORD_EXPIRED') return this.t('errorTempPasswordExpired')
      if (code === 'USER_LOGIN_FAILED') return this.t('errorLoginFailed')
      if (code === 'USER_NOT_LOGGED_IN') return this.t('errorNotLoggedIn')
      return (data && data.message) || this.t('loginFailed')
    }
  }
})
</script>

<style scoped>
/* Mobile-first: the embedded overlay is a true full-screen modal on
   phones (no margins, no rounded corners) and a centered card from
   640px upward. The inline (standalone) variant fills its host
   container — the StandaloneShell already provides padding around it
   on wide screens. */

.login-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.85);
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  z-index: 100;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.login-overlay--inline {
  position: relative;
  background: transparent;
  inset: auto;
  min-height: auto;
  width: 100%;
  display: flex;
  justify-content: stretch;
  align-items: flex-start;
}

/* Mobile: card fills the available area, no fancy rounding. */
.login-card {
  width: 100%;
  background: white;
  padding: 20px;
  border-radius: 0;
  min-height: 100%;
  box-shadow: none;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.login-overlay--inline .login-card {
  min-height: auto;
}

.loading {
  text-align: center;
  color: #6b7280;
  padding: 24px 0;
}

/* Tablet+: float the card back to a centered, rounded surface with a
   sensible width cap so reading lines don't get too long. */
@media (min-width: 640px) {
  .login-overlay {
    align-items: center;
    justify-content: center;
  }
  .login-overlay--inline {
    align-items: flex-start;
    justify-content: center;
  }
  .login-card {
    width: min(460px, calc(100vw - 32px));
    padding: 24px;
    border-radius: 12px;
    min-height: auto;
    max-height: calc(100dvh - 32px);
    overflow-y: auto;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
  }
}
</style>
